import { timeFromInt, timeToInt } from "time-number";

/**
 * Sorts an array of slot objects by a given time-string key.
 * @param {object[]} array - Array of slot objects.
 * @param {string} key - The property key containing the time string (e.g. "slotName").
 * @returns {object[]} A new sorted array.
 */
export function sortSlots(array, key) {
  if (array.length === 0) {
    return [];
  }
  return [...array].sort(function (a, b) {
    var x = timeToInt(a[key]);
    var y = timeToInt(b[key]);
    return x < y ? -1 : x > y ? 1 : 0;
  });
}

/**
 * Generates a slot array from a time range, preserving existing slot persons/values.
 * @param {number} startTime - Start time as integer seconds from midnight.
 * @param {number} endTime - End time as integer seconds from midnight.
 * @param {number} slotTime - Slot duration in minutes.
 * @param {object[]} existingSlots - Current slots array from state.
 * @returns {object[]} Sorted merged slots array.
 */
export function buildGeneratedSlots(startTime, endTime, slotTime, existingSlots) {
  var existingSlotNames = existingSlots.map((x) => x.slotName);
  var stateSlotNames = existingSlots.map((x) => x.slotName);
  var slots = [];

  for (
    var i = parseInt(startTime, 10);
    i <= parseInt(endTime, 10);
    i = i + 60 * slotTime
  ) {
    var slotName = timeFromInt(i, { format: 12 });
    var data = {};
    if (existingSlotNames.includes(slotName)) {
      var position = stateSlotNames.indexOf(slotName);
      data = existingSlots[position];
    } else {
      data = {
        slotName: timeFromInt(i, { format: 12 }),
        person: "",
        value: {},
      };
    }
    slots.push(data);
  }

  // Re-add any original slots that were not regenerated (outside new range)
  var missingOriginalsSlots = existingSlotNames.filter(
    (e) => !slots.map((x) => x.slotName).includes(e),
  );
  var missingOriginals = existingSlots.filter((x) =>
    missingOriginalsSlots.includes(x.slotName),
  );

  return sortSlots(slots.concat(missingOriginals), "slotName");
}

/**
 * Generates unslotted group time slots from a time range, preserving existing ones.
 * @param {number} startTime - Start time as integer seconds from midnight.
 * @param {number} endTime - End time as integer seconds from midnight.
 * @param {number} slotTime - Slot duration in minutes.
 * @param {object[]} existingUnslotedGroupTimes - Current unslotedGroupTimes from state.
 * @returns {object[]} Sorted merged unslotted group times array.
 */
export function buildGeneratedUnslottedGroupTimes(startTime, endTime, slotTime, existingUnslotedGroupTimes) {
  var stateSlotNames = existingUnslotedGroupTimes.map((x) => x.slotName);
  var slots = [];

  for (
    var i = parseInt(startTime, 10);
    i <= parseInt(endTime, 10);
    i = i + 60 * slotTime
  ) {
    var slotName = timeFromInt(i, { format: 12 });
    if (!stateSlotNames.includes(slotName)) {
      slots.push({ slotName: timeFromInt(i, { format: 12 }) });
    }
  }

  // Re-add any originals that were not in the new range
  var missingOriginalsSlots = stateSlotNames.filter(
    (e) => !slots.map((x) => x.slotName).includes(e),
  );
  var missingOriginals = existingUnslotedGroupTimes.filter((x) =>
    missingOriginalsSlots.includes(x.slotName),
  );
  console.log(missingOriginals);

  return sortSlots(slots.concat(missingOriginals), "slotName");
}

/**
 * Fills empty slot positions with unslotted members, creating new slots if needed.
 * @param {object[]} existingSlots - Current slots array from state.
 * @param {object[]} unslotedMembers - Current unslotedMembers from state.
 * @param {number} slotTime - Slot duration in minutes.
 * @returns {object[]} New slots array with members filled in.
 */
export function buildFilledLeftovers(existingSlots, unslotedMembers, slotTime) {
  var remainingslots = existingSlots.filter((x) => x.person === "").length;
  var existingSlotNames = existingSlots.map((x) => x.slotName);
  var stateSlotNames = existingSlots.map((x) => x.slotName);

  var startTime = 0;
  if (existingSlots.length > 0) {
    startTime = timeToInt(existingSlots[0].slotName);
    console.log(timeToInt(existingSlots[0].slotName));
  } else {
    startTime = 25200;
  }

  var slots = [];
  if (remainingslots < unslotedMembers.length) {
    var difference = unslotedMembers.length - remainingslots - 1;
    console.log(difference);
    for (
      var i = startTime;
      i <= startTime + difference * 600;
      i = i + 60 * slotTime
    ) {
      var slotName = timeFromInt(i, { format: 12 });
      var data = {};
      if (existingSlotNames.includes(slotName)) {
        var position = stateSlotNames.indexOf(slotName);
        data = existingSlots[position];
        difference++;
      } else {
        data = {
          slotName: timeFromInt(i, { format: 12 }),
          person: "",
          value: {},
        };
      }
      slots.push(data);
    }
  }

  var missingOriginalsSlots = existingSlotNames.filter(
    (e) => !slots.map((x) => x.slotName).includes(e),
  );
  var missingOriginals = existingSlots.filter((x) =>
    missingOriginalsSlots.includes(x.slotName),
  );
  slots = sortSlots(slots.concat(missingOriginals), "slotName");

  // Fill empty slots with unslotted members
  var newSlots = [];
  var unslotedMembersReversed = [...unslotedMembers].reverse();

  for (var j = 0; j < slots.length; j++) {
    var newdata = slots[j];
    if (unslotedMembersReversed.length === 0) {
      newSlots.push(newdata);
      continue;
    }
    if (newdata.person) {
      newSlots.push(newdata);
    } else {
      var unslotedMembersData = unslotedMembersReversed.pop();
      newdata.person =
        unslotedMembersData.fName + " " + unslotedMembersData.lName;
      newdata.value = unslotedMembersData;
      newSlots.push(newdata);
    }
  }

  console.log(remainingslots);
  return newSlots;
}
